import React, { useState, useEffect } from 'react';
import {
  FiCheck, FiX, FiRefreshCw, FiAlertTriangle, FiCheckCircle, FiInfo
} from 'react-icons/fi';
import { superAdminAPI } from '../../services/api';
import DataTable from '../../components/shared/DataTable';
import Modal from '../../components/shared/Modal';

const SmsOrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [masterAccount, setMasterAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [processingId, setProcessingId] = useState(null);
  const [feedback, setFeedback] = useState(null);

  // Modal Rejet
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState('Paiement Orange Money / Wave non vérifiable ou référence incorrecte');

  const fetchOrdersAndMaster = async () => {
    setLoading(true);
    try {
      const [ordersRes, masterRes] = await Promise.all([
        superAdminAPI.getSmsOrders({ status: statusFilter }),
        superAdminAPI.getMasterAccount().catch(() => ({ data: null }))
      ]);
      setOrders(ordersRes.data || []);
      setMasterAccount(masterRes.data || null);
    } catch (error) {
      console.error('Erreur chargement commandes SMS & compte Master:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrdersAndMaster();
  }, [statusFilter]);

  const masterAvailableBalance = Number(
    masterAccount?.lastKnownBatakiBalance ?? 
    masterAccount?.last_known_balance ?? 
    masterAccount?.master_remaining_credits ?? 
    masterAccount?.remainingCredits ?? 
    0
  );

  const handleApprove = async (order) => {
    if (masterAvailableBalance < order.sms_count) {
      setFeedback({
        type: 'error',
        text: `Validation impossible : Le compte principal BatakiPlus ne dispose actuellement que de ${masterAvailableBalance} SMS disponibles (${order.sms_count} SMS demandés par "${order.agency_name}"). L'attribution est bloquée en attente du réapprovisionnement réel sur BatakiPlus.`
      });
      return;
    }

    if (!window.confirm(`Confirmez-vous la réception du paiement de ${Number(order.price).toLocaleString('fr-FR')} XOF pour débiter ${order.sms_count} SMS du compte principal BatakiPlus (reste actuel: ${masterAvailableBalance} SMS) et créditer l'agence "${order.agency_name}" ?`)) {
      return;
    }

    setProcessingId(order.id);
    setFeedback(null);
    try {
      const res = await superAdminAPI.processSmsOrder(order.id, { action: 'approve' });
      setFeedback({
        type: 'success',
        text: res.data?.message || `Commande de ${order.sms_count} SMS validée avec succès pour ${order.agency_name} !`
      });
      fetchOrdersAndMaster();
    } catch (err) {
      setFeedback({
        type: 'error',
        text: err.response?.data?.error || 'Erreur lors de la validation de la commande.'
      });
    } finally {
      setProcessingId(null);
    }
  };

  const handleRejectSubmit = async (e) => {
    e.preventDefault();
    if (!rejectModal) return;

    setProcessingId(rejectModal.id);
    setFeedback(null);
    try {
      await superAdminAPI.processSmsOrder(rejectModal.id, {
        action: 'reject',
        rejectionReason: rejectReason,
      });
      setFeedback({
        type: 'success',
        text: `Commande rejetée. L'agence a été notifiée.`
      });
      setRejectModal(null);
      fetchOrdersAndMaster();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors du rejet de la commande.');
    } finally {
      setProcessingId(null);
    }
  };

  const fmt = (n) => Number(n || 0).toLocaleString('fr-FR');
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

  const columns = [
    {
      header: 'Agence',
      render: r => (
        <div>
          <div style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{r.agency_name}</div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            📞 {r.agency_phone || '—'} {r.agency_email ? `• ${r.agency_email}` : ''}
          </div>
          <div style={{ fontSize: 11, color: '#7c3aed', fontWeight: 600 }}>
            Solde actuel agence: 🪙 {r.agency_current_credits || r.agency_sms_credits || 0} SMS
          </div>
        </div>
      )
    },
    {
      header: 'Pack / Quantité',
      render: r => (
        <div>
          <span className="badge badge-info" style={{ fontWeight: 700, fontSize: 12 }}>
            🪙 {fmt(r.sms_count)} SMS
          </span>
          {r.pack_name && (
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>{r.pack_name}</div>
          )}
        </div>
      )
    },
    {
      header: 'Montant Dû',
      render: r => (
        <span style={{ fontWeight: 700, fontSize: 14, color: '#0f172a' }}>
          {fmt(r.price)} XOF
        </span>
      )
    },
    {
      header: 'Paiement (OM / Wave)',
      render: r => (
        <div style={{ fontSize: 12 }}>
          <div style={{ fontWeight: 600, textTransform: 'capitalize', color: '#ea580c' }}>
            📱 {r.payment_method?.replace('_', ' ')}
          </div>
          {r.payment_phone && (
            <div>Payeur : <strong>{r.payment_phone}</strong></div>
          )}
          <div style={{ marginTop: 2 }}>
            Réf: <code style={{ background: '#f1f5f9', padding: '2px 5px', borderRadius: 4, fontWeight: 700 }}>{r.payment_reference}</code>
          </div>
        </div>
      )
    },
    {
      header: 'Date & Statut Stock',
      render: r => {
        const hasEnoughStock = masterAvailableBalance >= r.sms_count;

        return (
          <div style={{ fontSize: 12 }}>
            <div>{fmtDate(r.created_at)}</div>
            {r.status === 'pending' && (
              <div style={{ marginTop: 4 }}>
                {hasEnoughStock ? (
                  <span style={{ color: '#16a34a', fontSize: 11, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                    <FiCheckCircle size={12} /> Stock Master OK ({fmt(masterAvailableBalance)} dispo)
                  </span>
                ) : (
                  <span style={{ color: '#dc2626', fontSize: 11, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                    <FiAlertTriangle size={12} /> Stock insuffisant ({fmt(masterAvailableBalance)} / {fmt(r.sms_count)} SMS)
                  </span>
                )}
              </div>
            )}
          </div>
        );
      }
    },
    {
      header: 'Statut',
      render: r => {
        let badgeColor = 'warning';
        let label = '⏳ En attente';
        if (r.status === 'approved') {
          badgeColor = 'success';
          label = '✅ Validée & Créditée';
        } else if (r.status === 'rejected') {
          badgeColor = 'danger';
          label = '❌ Rejetée';
        }

        return (
          <div>
            <span className={`badge badge-${badgeColor}`} style={{ fontWeight: 700 }}>
              {label}
            </span>
            {r.rejection_reason && (
              <div style={{ fontSize: 11, color: '#ef4444', marginTop: 2 }}>
                Motif : {r.rejection_reason}
              </div>
            )}
            {r.processed_at && (
              <div style={{ fontSize: 10, color: '#64748b' }}>
                Traité le {fmtDate(r.processed_at)}
              </div>
            )}
          </div>
        );
      }
    },
    {
      header: 'Action Super Admin',
      render: r => {
        if (r.status !== 'pending') {
          return (
            <span style={{ fontSize: 12, color: 'var(--text-secondary)', fontStyle: 'italic' }}>
              Traitée
            </span>
          );
        }

        const hasEnoughStock = masterAvailableBalance >= r.sms_count;

        return (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'nowrap', alignItems: 'center' }}>
            <button
              className="btn btn-success btn-sm"
              disabled={!hasEnoughStock || processingId === r.id}
              onClick={() => handleApprove(r)}
              title={hasEnoughStock ? "Valider le paiement et débiter le compte master pour créditer l'agence" : `Validation bloquée : Le compte BatakiPlus ne dispose que de ${masterAvailableBalance} SMS (${r.sms_count} requis)`}
              style={{
                padding: '4px 10px',
                fontSize: 11,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                background: hasEnoughStock ? '#16a34a' : '#94a3b8',
                borderColor: hasEnoughStock ? '#16a34a' : '#94a3b8',
                cursor: hasEnoughStock ? 'pointer' : 'not-allowed',
                opacity: hasEnoughStock ? 1 : 0.6
              }}
            >
              <FiCheck /> Valider
            </button>
            <button
              className="btn btn-danger btn-sm"
              disabled={processingId === r.id}
              onClick={() => setRejectModal(r)}
              title="Rejeter la demande"
              style={{ padding: '4px 8px', fontSize: 11 }}
            >
              <FiX /> Rejeter
            </button>
          </div>
        );
      }
    }
  ];

  return (
    <div className="page-container" style={{ paddingBottom: 40 }}>
      {/* Header */}
      <div className="page-header">
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ background: '#7c3aed', color: '#fff', padding: '3px 9px', borderRadius: 20, fontSize: 11, fontWeight: 700 }}>
              🛡️ SUPER ADMIN
            </span>
            <h1 style={{ margin: 0, fontSize: 22 }}>Demandes d'Achat de Packs SMS (Orange Money & Wave)</h1>
          </div>
          <p style={{ margin: '4px 0 0 0', fontSize: 13, color: 'var(--text-secondary)' }}>
            Vérifiez les paiements reçus des agences et validez l'attribution sous condition de stock BatakiPlus suffisant
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn btn-secondary btn-sm" onClick={fetchOrdersAndMaster}>
            <FiRefreshCw style={{ marginRight: 4 }} /> Actualiser
          </button>
        </div>
      </div>

      {/* BANNIÈRE SOLDE RÉEL BATAKIPLUS */}
      <div className="card" style={{
        marginBottom: 20,
        background: masterAvailableBalance > 50 ? '#f0fdf4' : '#fff5f5',
        borderLeft: `5px solid ${masterAvailableBalance > 50 ? '#16a34a' : '#dc2626'}`,
        padding: '14px 20px',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: masterAvailableBalance > 50 ? '#15803d' : '#991b1b', textTransform: 'uppercase' }}>
              Solde Réel Passerelle BatakiPlus (Compte Principal)
            </div>
            <div style={{ fontSize: 22, fontWeight: 800, color: masterAvailableBalance > 50 ? '#166534' : '#991b1b', marginTop: 2 }}>
              🪙 {fmt(masterAvailableBalance)} SMS restants sur BatakiPlus
            </div>
            <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
              Règle stricte : Toute commande supérieure au solde restant ({fmt(masterAvailableBalance)} SMS) est bloquée en attente du réapprovisionnement externe sur BatakiPlus.
            </div>
          </div>
          <div style={{ background: '#e2e8f0', padding: '4px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600, color: '#475569' }}>
            Lecture seule
          </div>
        </div>
      </div>

      {/* Feedback message */}
      {feedback && (
        <div className="card" style={{
          marginBottom: 20,
          background: feedback.type === 'success' ? '#dcfce7' : '#fee2e2',
          borderLeft: `5px solid ${feedback.type === 'success' ? '#22c55e' : '#ef4444'}`,
          padding: 14
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <span style={{ fontWeight: 600, color: feedback.type === 'success' ? '#15803d' : '#b91c1c', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
              {feedback.type === 'success' ? <FiCheckCircle size={18} /> : <FiAlertTriangle size={18} />}
              {feedback.text}
            </span>
            <button className="btn btn-sm btn-secondary" onClick={() => setFeedback(null)}>OK</button>
          </div>
        </div>
      )}

      {/* Filtres statut */}
      <div className="tabs" style={{ marginBottom: 20 }}>
        {[
          { key: '', label: 'Toutes les commandes' },
          { key: 'pending', label: '⏳ En attente de validation' },
          { key: 'approved', label: '✅ Validées & Créditées' },
          { key: 'rejected', label: '❌ Rejetées' },
        ].map(t => (
          <button
            key={t.key}
            className={`tab ${statusFilter === t.key ? 'active' : ''}`}
            onClick={() => setStatusFilter(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Tableau des commandes */}
      <div className="card">
        <div className="card-header">
          <h2 style={{ fontSize: 16 }}>Historique des commandes ({orders.length})</h2>
        </div>
        {loading ? (
          <div className="loading" style={{ padding: 40 }}><div className="spinner" /></div>
        ) : (
          <div className="table-container">
            <DataTable columns={columns} data={orders} emptyMessage="Aucune commande trouvée." />
          </div>
        )}
      </div>

      {/* MODAL REJET COMMANDE */}
      {rejectModal && (
        <Modal isOpen={true} onClose={() => setRejectModal(null)} title="❌ Rejeter la commande de packs SMS">
          <form onSubmit={handleRejectSubmit}>
            <div style={{ background: '#fee2e2', padding: 12, borderRadius: 6, marginBottom: 16, borderLeft: '4px solid #ef4444' }}>
              <div style={{ fontWeight: 700, color: '#991b1b' }}>Agence : {rejectModal.agency_name}</div>
              <div style={{ fontSize: 13, color: '#b91c1c' }}>
                Commande : <strong>{fmt(rejectModal.sms_count)} SMS</strong> ({fmt(rejectModal.price)} XOF) • Réf : {rejectModal.payment_reference}
              </div>
            </div>

            <div className="form-group" style={{ marginBottom: 16 }}>
              <label style={{ fontWeight: 600, fontSize: 13, marginBottom: 4, display: 'block' }}>
                Motif du rejet (notifié à l'agence) *
              </label>
              <textarea
                className="form-control"
                rows={3}
                required
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
              <button type="button" className="btn btn-secondary" onClick={() => setRejectModal(null)}>
                Annuler
              </button>
              <button type="submit" className="btn btn-danger">
                Confirmer le Rejet
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
};

export default SmsOrdersPage;
